import { EventEmitter } from "events"
import dispatcher from "../dispatcher"
import strings from "../localization/strings"
import MobileDetect from "mobile-detect"
const md = new MobileDetect(navigator.userAgent)
import * as utils from "../helpers/utils"
import { EHostType, IAlertDialogContent, IConfirmDialogContent, IFailedCalcAccrualDataInfoDialogContent, INewCustomReportDialogContent, INoteDrawerContent, IPunchExceptionReviewDialogContent, ISuspensePunchReviewDialogContent, ITextProgressSnackbarContent, ITimecardSequenceWarningDialogContent } from '../models'
import FirestoreStore from "./FirestoreStore"
import { IAccountManagerFESettings } from "../models/data/account_manager/AccountManagerFESettings"

const dashboardNavItems = [
	{
		name: "I9",
		filters: ["All Records", "Message Sent", "Section 1 Pending", "Section 2 Pending", "Section 2 Scheduled", "Section 2 Complete"]
	},
	{
		name: "Map",
		filters: [
			{ value: "Today", label: strings.Today, route: "today" },
			{ value: "This Week", label: strings.This_Week, route: "thisWeek" },
			{
				value: "This Month",
				label: strings.This_Month,
				route: "thisMonth"
			},
			{ value: "Last Week", label: strings.Last_Week, route: "lastWeek" },
			{
				value: "Last Month",
				label: strings.Last_Month,
				route: "lastMonth"
			}
		]
	}
]

const hierarchyNavItems = ["details", "children", "users", "employees", "positions", "location", "records", "devices", "settings"]
const accountNavItems = ["Users", "HierarchyDefinitions", "Positions", "Settings", "Billing"]

class AppStateStore extends EventEmitter {
	_refreshing: boolean
	expandNav: string
	dashboardNavItems: any[]
	accountNavItems: any[]
	hierarchyNavItems: any[]
	progressSnackbarContent: string
	textProgressSnackbarContent: ITextProgressSnackbarContent
	bannerAlertContent: string
	alertDialogContent: IAlertDialogContent
	confirmDialogContent: IConfirmDialogContent
	timecardSequenceWarningDialogContent: ITimecardSequenceWarningDialogContent
	noteDrawerContent: INoteDrawerContent
	punchExceptionReviewDialogContent: IPunchExceptionReviewDialogContent
	suspensePunchReviewDialogContent: ISuspensePunchReviewDialogContent
	freeAccountDataLimitUpgradeDialogContent: string
	failedCalcAccrualDataInfoDialogContent: IFailedCalcAccrualDataInfoDialogContent[]
	newCustomReportDialogContent: INewCustomReportDialogContent
	frontEndSettings: IAccountManagerFESettings
	hideScheduleAutoAssign: boolean
	hideSchedulePatternTemplate: boolean
	hideAgreementFeature: boolean
	mobileIsUsingShiftPositionId: boolean
	notTicksExistResponse: string
	failedToCheckConcurrency: string
	hostType: EHostType
	perEmployeePerMonthRateInitValue: number
	allowAccontLevelClockDevice: boolean
	registrationUserFirstName: string
	registrationUserLastName: string
	registrationEmail: string
	constructor() {
		super()
		this.setMaxListeners(0)
		this._refreshing = false
		this.expandNav = utils.getCookie("expandNav") || "expanded"
		this.dashboardNavItems = dashboardNavItems
		this.accountNavItems = accountNavItems
		this.hierarchyNavItems = hierarchyNavItems
		this.progressSnackbarContent = ""
		this.textProgressSnackbarContent = null
		this.bannerAlertContent = ""
		this.alertDialogContent = null
		this.hideScheduleAutoAssign = false
		this.hideSchedulePatternTemplate = true
		this.hideAgreementFeature = false
		this.mobileIsUsingShiftPositionId = true
		this.notTicksExistResponse = "TicksSinceChange is missing"
		this.failedToCheckConcurrency = "Concurrency check failed"
		this.hostType = ["", ""].includes(window.location.host) ? EHostType.OnShift : EHostType.Findd
		this.perEmployeePerMonthRateInitValue = 3.48
		this.allowAccontLevelClockDevice = true
		this.registrationUserFirstName = null
		this.registrationUserLastName = null
		this.registrationEmail = null
	}

	getLayoutHeaderColor() {
		return this.getFrontEndSettings().UISettings.PrimaryColor //this.hostType === EHostType.Findd ? blueGrey500 : "#003560"
	}

	getDrawerHeaderColor() {
		return this.getFrontEndSettings().UISettings.AccentColor //this.hostType === EHostType.Findd ? amber500 : "#73B63F"
	}

	getHostType() {
		return this.hostType
	}

	isLaptop() {
		return window.innerWidth < 1600
	}

	isMobile() {
		return md.phone() && md.phone().length > 0
	}

	isFindd() {
		let domain = document.domain.toLowerCase()
		return domain.includes("findd") || domain.includes("localhost")
	}

	setRefreshing(isRefreshing) {
		this._refreshing = isRefreshing
		this.emit("change")
	}

	getRefreshing() {
		return this._refreshing
	}

	getLeftNavWidth() {
		return this.expandNav === "expanded" ? 224 : 80
	}

	getTopBarHeight() {
		return 70
	}

	getContentWidth() {
		return window.innerWidth - this.getLeftNavWidth()
	}

	toggleLeftNav() {
		const expandNav = this.expandNav === "expanded" ? "collapsed" : "expanded"
		this.expandNav = expandNav
		this.emit("change")
		localStorage.setItem("findd_expandNav", expandNav)
	}

	getExpandNav() {
		return this.expandNav
	}

	getDateFilterRangeText(routeParam, selected) {
		if (!routeParam) return "Today"
		return this.dashboardNavItems.filter((item) => item.name === selected)[0].filters.filter((filter) => filter.route === routeParam)[0].value
	}

	setHierarchyNavItems(navItems) {
		this.hierarchyNavItems = navItems
		this.emit("change")
	}

	getHierarchyNavItems() {
		return this.hierarchyNavItems
	}

	setProgressSnackbarContent(progressSnackbarContent: string, cb = null) {
		this.progressSnackbarContent = progressSnackbarContent
		cb && typeof cb === "function" && cb()
		this.emit("progress_change")
	}

	getProgressSnackbarContent() {
		return this.progressSnackbarContent
	}

	setTextProgressSnackbarContent(textProgressSnackbarContent: ITextProgressSnackbarContent, cb = null) {
		this.textProgressSnackbarContent = textProgressSnackbarContent
		cb && typeof cb === "function" && cb()
		this.emit("textprogress_change")
	}

	getTextProgressSnackbarContent() {
		return this.textProgressSnackbarContent
	}

	setBannerAlertContent(bannerAlertContent: string, cb = null) {
		this.bannerAlertContent = bannerAlertContent
		cb && typeof cb === "function" && cb()
		this.emit("banner_alert_change")
	}

	getBannerAlertContent() {
		return this.bannerAlertContent
	}

	setAlertDialogContent(alertDialogContent: IAlertDialogContent) {
		this.alertDialogContent = alertDialogContent
		this.emit("alert_change")
	}

	setConfirmDialogContent(confirmDialogContent: IConfirmDialogContent) {
		this.confirmDialogContent = confirmDialogContent
		this.emit("confirm_change")
	}

	setTimecardSequenceWarningDialogContent(timecardSequenceWarningDialogContent: ITimecardSequenceWarningDialogContent) {
		if (timecardSequenceWarningDialogContent !== null) {
			this.setProgressSnackbarContent(null)
			this.setTextProgressSnackbarContent(null)
			FirestoreStore.setAsyncSessionId(null)
		}
		this.timecardSequenceWarningDialogContent = timecardSequenceWarningDialogContent
		this.emit("timecard_sequence_warning_change")
	}

	setNoteDrawerContent(noteDrawerContent: INoteDrawerContent) {
		this.noteDrawerContent = noteDrawerContent
		this.emit("note_drawer_change")
	}

	openMessageCenterDrawer() {
		this.emit("open_message_center")
	}

	openAccountBillingDrawer() {
		this.emit("open_account_billing")
	}

	setPunchExceptionReviewDialogContent(punchExceptionReviewDialogContent: IPunchExceptionReviewDialogContent) {
		this.punchExceptionReviewDialogContent = punchExceptionReviewDialogContent
		this.emit("punch_exception_review_dialog_change")
	}

	setSuspensePunchReviewDialogContent(suspensePunchReviewDialogContent: ISuspensePunchReviewDialogContent) {
		this.suspensePunchReviewDialogContent = suspensePunchReviewDialogContent
		this.emit("suspense_punch_review_dialog_change")
	}

	setFreeAccountDataLimitUpgradeDialogContent(freeAccountDataLimitUpgradeDialogContent: string) {
		this.freeAccountDataLimitUpgradeDialogContent = freeAccountDataLimitUpgradeDialogContent
		this.emit("free_account_data_limit_upgrade_dialog_change")
	}

	setFailedCalcAccrualDataInfoDialogContent(failedCalcAccrualDataInfoDialogContent: IFailedCalcAccrualDataInfoDialogContent[]) {
		this.failedCalcAccrualDataInfoDialogContent = failedCalcAccrualDataInfoDialogContent
		this.emit("failed_calc_accrual_data_info_dialog_change")
	}

	setNewCustomReportDialogContent(newCustomReportDialogContent: INewCustomReportDialogContent) {
		this.newCustomReportDialogContent = newCustomReportDialogContent
		this.emit("new_custom_report_dialog_change")
	}

	setGetPayrollTimeByAction() {
		this.emit("get_payroll_time_by_action")
	}

	setFrontEndSettings(frontEndSettings) {
		this.frontEndSettings = frontEndSettings
		this.emit("frontEndSettings_change")
	}

	getFrontEndSettings(): IAccountManagerFESettings {
		if (!this.frontEndSettings) {
			return {
				UISettings: {
					LogoURL: "/assets/logo.png",
					HeaderLogoURL: "/assets/findd-logo.png",
					LoginLogoURL: "/assets/logo.png",
					HeaderCollapsedLogoURL: "/assets/dd-logo.png",
					PrimaryColor: "#607D8B",
					AccentColor: "#FB8C00",
					TextColor: "#616161",
					MarketingButtonColor: "#1181F2",
					MarketingTextColor: "#ffffff",
					HeaderSecondaryColor: "#455A64",
					HeaderTextColor: "#edf6fb",
					MenuTextColor: "#ECEFF1",
					MenuBackgroundColor: "#455A64",
					MenuSelectedBackgroundColor: "#263238",
					MenuIconColor: "#90A4AE",
					SchedulingPaywallMarketingURL: "https://findd-prod-assets.s3.us-west-2.amazonaws.com/web/paywall-marketing/scheduling.html",
					LeaveTrackingPaywallMarketingURL: "https://findd-prod-assets.s3.us-west-2.amazonaws.com/web/paywall-marketing/leavetracking.html",
					AccountRegistrationHTMLURL: "https://findd-prod-assets.s3.us-west-2.amazonaws.com/web/account_registration.html",
					AccountRegistrationImageURL: "https://findd-prod-assets.s3.us-west-2.amazonaws.com/web/email-graphic.png",
					LoginHTMLURL: "https://findd-prod-assets.s3.us-west-2.amazonaws.com/web/login.html",
					MenuSelectedTextColor: "#000000",
					MenuBarBackgroundColor: "#607D8B",
				}
			}
		}

		return this.frontEndSettings
	}

	triggerGetFreeAccountMetricsData() {
		this.emit("trigger_getFreeAccountMetricsData")
	}

	setNoAccessPage() {
		this.emit("no_access_page")
	}

	handleActions(action) {
		switch (action.type) {
			case "XX": {
				break
			}
			case "UPDATE_HIERARCHY_NAV_ITEMS": {
				this.setHierarchyNavItems(action.data)
				break
			}
			case "UPDATE_REFRESHING": {
				this.setRefreshing(action.data)
				break
			}
			case "TOGGLE_EXPAND_LEFT_NAV": {
				this.toggleLeftNav()
				break
			}
			case "UPDATE_FRONT_END_SETTINGS": {
				this.setFrontEndSettings(action.data)
				break
			}
		}
	}
}

const appStateStore = new AppStateStore()
dispatcher.register(appStateStore.handleActions.bind(appStateStore))
export default appStateStore
